    import React, { useState } from "react";
    import {
    TableContainer, Table, TableHead, TableRow, TableCell,
    TableBody, Button, Box, Typography, IconButton, Menu,
    MenuItem, TextField,
    } from "@mui/material";
    import EditIcon from "@mui/icons-material/Edit";
    import DeleteIcon from "@mui/icons-material/Delete";
    import FilterListIcon from "@mui/icons-material/FilterList";

const EnquiryImportTable = ({ 
    columns, 
    data, 
    onEdit, 
    onDelete, 
    customCellRender, 
    disableDelete, 
    itemsPerPage,
    totalPages,      
    currentPage,     
    onPageChange,    
    disableSorting   
    }) => {
    const [filters, setFilters] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeFilterColumn, setActiveFilterColumn] = useState(null);

    // --- CLIENT SIDE SORTING LOGIC ---
    let processedData = data;
    
    // Only sorts locally if disableSorting is false. 
    // In our case, parent passes true because sorting is done on server.
    if (!disableSorting) {
        const getStatusSortWeight = (statusValue) => {
        if (!statusValue) return 999; // Put empty/undefined statuses at the end
        
        const status = String(statusValue).toLowerCase().trim();

        // Sorting Order (Lower number = Higher Priority)
        // if (status === "pending") return 1;
        // if (status === "no answer") return 2;
        // if (status === "call back") return 3;
        // if (status === "not interested") return 4;
        // if (status === "send info") return 5;
        // if (status === "busy") return 6;
        // if (status === "interested") return 7;

        // Fallback for any unexpected statuses
        return 99; 
        };

        processedData = [...data].sort((a, b) => {
        const weightA = getStatusSortWeight(a.status);
        const weightB = getStatusSortWeight(b.status);
        return weightA - weightB; // Ascending order (1 comes before 7)
        });
    }
    // -----------------------------------------

    
     const filteredData = processedData.filter((row) =>
        columns.every((col) => {
        const filterValue = filters[col.field] || "";
        // Check for null/undefined values before filtering
        const cellValue = row[col.field]; 
        if (cellValue === null || cellValue === undefined) return !filterValue; 
        return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
        })
    );

    // FIX: Apply filteredData regardless of sorting, only slice if client-side pagination
    const currentItems = (disableSorting) 
        ? filteredData 
        : filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    
    // Use passed totalPages if available, otherwise calculate locally
    const displayTotalPages = (totalPages !== undefined) 
        ? totalPages 
        : Math.ceil(filteredData.length / itemsPerPage);


    const handleFilterChange = (value) => {
        if (activeFilterColumn) {
        setFilters((prev) => ({
            ...prev,
            [activeFilterColumn]: value,
        }));
        }
    };

    const handleFilterIconClick = (event, column) => {
        setAnchorEl(event.currentTarget);
        setActiveFilterColumn(column.field);
    };

    const handleCloseFilterMenu = () => {
        setAnchorEl(null);
    };

    return (
        <>
        <TableContainer
            sx={{
            display: "flex", justifyContent: "flex-start",
            borderCollapse: "separate", borderSpacing: "0 16px",
            backgroundColor: "#fff", borderRadius: "4px", width: "100%",
            overflowY: "auto", boxShadow: "none", p: 5,
            }}
        >
            <Table
            sx={{
                borderCollapse: "separate", borderSpacing: "0 16px",
                backgroundColor: "#fff", borderRadius: "4px", width: "100%",
            }}
            >
            <TableHead sx={{ backgroundColor: "#fb7102" }}>
                <TableRow>
                {columns.map((col, index) => (
                    <TableCell
                    key={col.field}
                    sx={{
                        fontSize: "0.9rem", fontFamily: 'Josefin Sans',
                        color: "white", fontWeight: 600,
                        borderTopLeftRadius: index === 0 ? "10px" : "0",
                        borderBottomLeftRadius: index === 0 ? "10px" : "0",
                        borderTopRightRadius: index === columns.length - 1 ? "10px" : "0",
                        borderBottomRightRadius: index === columns.length - 1 ? "10px" : "0",
                        borderTop: "1px solid rgba(0, 0, 0, 0.1)",
                        borderLeft: index === 0 ? "1px solid rgba(0, 0, 0, 0.1)" : "none",
                        borderRight: index === columns.length - 1 ? "1px solid rgba(0, 0, 0, 0.1)" : "none",
                        borderBottom: "5px solid #fbe3bd",
                        padding: "16px",
                        textAlign: col.align || "left",
                    }}
                    >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {col.headerName}
                        <IconButton size="small" onClick={(e) => handleFilterIconClick(e, col)} sx={{ ml: 1 }}>
                        <FilterListIcon sx={{ color: "white" }} />
                        </IconButton>
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && activeFilterColumn === col.field}
                        onClose={handleCloseFilterMenu}
                    >
                        <MenuItem>
                        <TextField
                            variant="outlined" size="small"
                            value={filters[col.field] || ""}
                            onChange={(e) => handleFilterChange(e.target.value)}
                            placeholder={`Filter ${col.headerName}`}
                        />
                        </MenuItem>
                    </Menu>
                    </TableCell>
                ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {currentItems.map((row, rowIndex) => (
                <TableRow
                    key={row.id || rowIndex}
                    sx={{
                    transition: "transform 0.4s ease-in-out, box-shadow 0.4s ease-in-out, background-color 0.4s ease-in-out",
                    "&:hover": {
                        transform: "scaleY(1.05)",
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
                        backgroundColor: "#f5f5f5",
                    },
                    }}
                >
                    {columns.map((col, colIndex) => {
                    const isFirst = colIndex === 0;
                    const isLast = colIndex === columns.length - 1;
                    let cellContent = row[col.field];

                    if (col.field === "password" && cellContent) cellContent = "•".repeat(cellContent.length);

                    if (col.renderCell) {
                        const cellParams = { row, field: col.field, id: row.id };
                        cellContent = col.renderCell(cellParams);
                    } else if (col.field === "actions") {
                        const isDeleteDisabled = typeof disableDelete === 'function' && disableDelete(row);
                        cellContent = (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <EditIcon
                            sx={{ mr: 3, color: "#3b71ca", cursor: "pointer" }}
                            onClick={() => onEdit(row)}
                            />
                            <DeleteIcon
                            sx={{
                                color: isDeleteDisabled ? "#ccc" : "#fb7102",
                                cursor: isDeleteDisabled ? "not-allowed" : "pointer",
                            }}
                            onClick={isDeleteDisabled ? undefined : () => onDelete(row.id)}
                            />
                        </Box>
                        );
                    } else if (customCellRender) {
                        cellContent = customCellRender(row, col);
                    }

                    return (
                        <TableCell
                        key={`${row.id}-${col.field}`}
                        sx={{
                            fontSize: "0.9rem", padding: "16px", fontFamily: "Josefin Sans",
                            color: "black",
                            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
                            borderLeft: isFirst ? "1px solid rgba(0, 0, 0, 0.1)" : "none",
                            borderRight: isLast ? "1px solid rgba(0, 0, 0, 0.1)" : "none",
                            borderBottom: "5px solid #faf0e0",
                            borderTopLeftRadius: isFirst ? "10px" : "0",
                            borderBottomLeftRadius: isFirst ? "10px" : "0",
                            borderTopRightRadius: isLast ? "10px" : "0",
                            borderBottomRightRadius: isLast ? "10px" : "0",
                            textAlign: col.align || "left",
                            overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                        }}
                        >
                        {cellContent}
                        </TableCell>
                    );
                    })}
                </TableRow>
                ))}
            </TableBody>
            </Table>
        </TableContainer>

        {/* Pagination Controls */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            sx={{
                color: "#aaa", borderRadius: "4px", margin: "0 10px", height: "30px", fontSize: "15px",
                border: "3px solid rgb(132, 160, 230)", minWidth: "35px",
                "&:hover": { backgroundColor: "#fff", color: "#000", border: "3px solid rgb(132, 160, 230)" },
                "&:disabled": { border: "3px solid rgb(132, 160, 230)", color: "#aaa", cursor: "not-allowed" },
            }}
            >
            {"<"}
            </Button>
            <span style={{ margin: "0 10px", fontFamily: "Josefin Sans" }}>
            {currentPage} of {displayTotalPages}
            </span>
            <Button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === displayTotalPages}
            sx={{
                border: "3px solid rgb(132, 160, 230)", color: "black", borderRadius: "4px",
                margin: "0 10px", height: "30px", fontSize: "15px", minWidth: "35px",
                "&:hover": { backgroundColor: "#fff", color: "#000", border: "3px solid rgb(132, 160, 230)" },
            }}
            >
            {">"}
            </Button>
        </div>
        </>
    );
    };

export default EnquiryImportTable;